import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import { 
  Calendar, 
  Clock, 
  User, 
  Tag, 
  ArrowLeft, 
  Share2, 
  BookOpen, 
  MessageSquare,
  Sparkles,
  Edit3,
  Check,
  ChevronRight
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FounderConsultationModal from '@/components/FounderConsultationModal';
import { INITIAL_BLOGS } from '@/data/blogsData';

const LOCAL_STORAGE_BLOGS = 'stellars_custom_blogs';
const ADMIN_AUTH_SESSION_KEY = 'stellars_admin_authenticated';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const savedAuth = sessionStorage.getItem(ADMIN_AUTH_SESSION_KEY);
    if (savedAuth === 'true') {
      setIsAdmin(true);
    }

    let allBlogs = INITIAL_BLOGS;
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_BLOGS);
      if (stored) {
        allBlogs = JSON.parse(stored);
      }
    } catch (e) {
      console.error("Failed to load local blogs", e);
    }

    const found = allBlogs.find(b => b.slug === slug || b.id === slug);
    if (found) {
      setBlog(found);
    } else {
      setBlog(allBlogs[0] || INITIAL_BLOGS[0]);
    }
  }, [slug]);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!blog) {
    return (
      <div className="min-h-screen bg-[#181F32] flex items-center justify-center text-white font-sans">
        <p className="text-sm text-white/70">Loading article...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#181F32] font-sans text-white">
      <Helmet>
        <title>{blog.title} | Stellars Estates Blog</title>
        <meta name="description" content={blog.excerpt} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:image" content={blog.imageUrl} />
      </Helmet>

      <Header />

      {/* Article Header & Navigation */}
      <section className="bg-[#181F32] text-white pt-10 pb-12 sm:py-16 border-b border-white/10 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-xs text-white/50">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link to="/blogs" className="hover:text-white transition-colors">Blogs</Link>
            <ChevronRight size={12} />
            <span className="text-[#F3453F] font-semibold truncate max-w-xs">{blog.topic}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span className="px-3.5 py-1 rounded-full bg-[#F3453F]/20 text-[#F3453F] border border-[#F3453F]/40 text-xs font-bold uppercase tracking-wider">
              {blog.topic}
            </span>
            <span className="text-xs text-white/60 flex items-center gap-1">
              <Calendar size={12} /> {blog.date}
            </span>
            <span className="text-xs text-white/60 flex items-center gap-1">
              <Clock size={12} /> {blog.readTime}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white font-heading leading-tight">
            {blog.title}
          </h1>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-white/10 text-xs text-white/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F3453F]/20 border border-[#F3453F]/40 flex items-center justify-center font-bold text-[#F3453F] text-sm">
                SS
              </div>
              <div>
                <p className="font-bold text-white text-sm">{blog.author}</p>
                <p className="text-[11px] text-white/50">Founder & Chief Growth Strategist</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isAdmin && (
                <button
                  onClick={() => navigate('/blogs')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F3453F]/20 hover:bg-[#F3453F] border border-[#F3453F]/40 text-xs font-bold text-[#F3453F] hover:text-white transition-all"
                >
                  <Edit3 size={13} />
                  <span>Edit in Admin Panel</span>
                </button>
              )}

              <button
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold text-white/80 transition-colors"
              >
                {copied ? <Check size={14} className="text-green-400" /> : <Share2 size={14} />}
                <span>{copied ? 'Link Copied!' : 'Share Article'}</span>
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* Main Cover Image */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 relative z-10">
        <div className="rounded-2xl overflow-hidden border border-white/15 shadow-2xl bg-[#141A29]">
          <img 
            src={blog.imageUrl} 
            alt={blog.title} 
            className="w-full h-[320px] sm:h-[480px] object-cover"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80";
            }}
          />
        </div>
      </div>

      {/* Article Content Body */}
      <section className="py-12 sm:py-16 bg-[#181F32]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Executive Summary Callout */}
          <div className="p-6 sm:p-8 rounded-2xl bg-[#141A29] border-l-4 border-[#F3453F] space-y-2.5 shadow-lg">
            <span className="text-xs font-bold text-[#F3453F] uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles size={14} />
              Executive Strategy Summary
            </span>
            <p className="text-base sm:text-lg text-white/90 font-sans italic leading-relaxed">
              "{blog.excerpt}"
            </p>
          </div>

          {/* Markdown Formatted Body */}
          <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-white prose-h1:text-3xl prose-h2:text-2xl prose-h2:text-[#F3453F] prose-h3:text-xl prose-p:text-white/85 prose-p:leading-relaxed prose-p:text-base sm:prose-p:text-lg prose-li:text-white/80 prose-strong:text-white prose-table:border-collapse prose-th:border prose-th:border-white/10 prose-th:p-3 prose-th:bg-white/5 prose-td:border prose-td:border-white/10 prose-td:p-3 prose-pre:bg-[#141A29] prose-pre:border prose-pre:border-white/10">
            <ReactMarkdown>{blog.content}</ReactMarkdown>
          </div>

          {/* Keywords & Tags */}
          <div className="pt-8 border-t border-white/10 space-y-3">
            <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider flex items-center gap-1.5">
              <Tag size={12} className="text-[#F3453F]" />
              Article Tags & Topics
            </h3>
            <div className="flex flex-wrap gap-2">
              {blog.keywords && blog.keywords.map((kw, idx) => (
                <span key={idx} className="px-3 py-1 rounded-lg bg-[#141A29] border border-white/10 text-xs font-semibold text-white/70">
                  #{kw}
                </span>
              ))}
            </div>
          </div>

          {/* Author Bio Card */}
          <div className="p-6 sm:p-8 rounded-2xl bg-[#141A29] border border-white/10 flex flex-col sm:flex-row items-center sm:items-start gap-6 shadow-xl">
            <div className="w-16 h-16 rounded-full bg-[#F3453F]/20 border border-[#F3453F]/40 flex items-center justify-center text-xl font-bold text-[#F3453F] shrink-0">
              SS
            </div>
            <div className="space-y-2 text-center sm:text-left">
              <h3 className="text-xl font-bold text-white font-heading">Written by {blog.author}</h3>
              <p className="text-xs text-[#F3453F] font-bold">Founder & Chief Growth Strategist, Stellars Estates</p>
              <p className="text-xs sm:text-sm text-white/75 font-sans leading-relaxed">
                Sahil Sharma specializes in architecting predictable revenue engines for real estate developers, commercial brokerages, and modern enterprises through precision performance marketing and autonomous AI agents.
              </p>
            </div>
          </div>

          {/* Consultation CTA Banner */}
          <div className="p-8 sm:p-10 rounded-2xl bg-gradient-to-r from-[#141A29] via-[#181F32] to-[#141A29] border border-[#F3453F]/40 text-center space-y-5 shadow-2xl">
            <span className="inline-block px-3.5 py-1 rounded-full bg-[#F3453F]/20 border border-[#F3453F]/40 text-[#F3453F] text-xs font-bold uppercase tracking-wider">
              Strategic Partnership
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">
              Ready to Implement These Growth Strategies?
            </h3>
            <p className="text-sm sm:text-base text-white/80 max-w-xl mx-auto font-sans leading-relaxed">
              Book a direct 1-on-1 strategy call with Founder Sahil Sharma to audit your advertising campaigns and automate your lead qualification pipeline.
            </p>
            <div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-8 py-4 rounded-xl bg-[#F3453F] hover:bg-[#D93833] text-white font-bold text-sm sm:text-base transition-all shadow-xl shadow-[#F3453F]/25 inline-flex items-center gap-2"
              >
                <MessageSquare size={18} />
                <span>Book Growth Audit with Sahil Sharma</span>
              </button>
            </div>
          </div>

          {/* Back to all blogs button */}
          <div className="text-center pt-4">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 text-xs font-bold text-white/70 hover:text-white px-4 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <ArrowLeft size={14} />
              <span>Back to All Blog Articles & Playbooks</span>
            </Link>
          </div>

        </div>
      </section>

      <Footer />

      <FounderConsultationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default BlogDetailPage;
